import { useMemo } from 'react';
import SideBarModal from '../modal/SideBarModal';
import { EmployeeToBusiness, Section, SectionRow } from '../../interfaces';
import useUser from '../../hooks/global/useUser';
import { formatDollarAmount } from '../../utils/global/helper';
import {
  getCommissionEmployees,
  getTextForCategory,
  getTextForCommissionType,
} from '../../helpers/items';
import { ItemWithCommissions } from '../../types/items';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  item: ItemWithCommissions | null | undefined;
}

const getName = (employee: EmployeeToBusiness) =>
  employee.displayName || employee.user?.firstName || 'Unnamed';

function ItemSideBarModal({ item, isOpen, onClose }: Props) {
  const { user } = useUser();
  const name = item?.name || 'Item Detail';

  const productTaxRate = user?.business?.businessTaxRates?.productTaxRate || 0;
  const employees = getCommissionEmployees(user?.business?.employees);

  const employeeCommissions = useMemo(() => {
    const commissionsMap: Record<
      string,
      {
        name: string;
        rate: number;
      }
    > = {};
    if (!employees.length || !item) return commissionsMap;

    employees?.forEach(employee => {
      commissionsMap[employee.employeeId] = {
        name: getName(employee),
        rate:
          item.employeeCommissions?.find(
            c => c.employeeId === employee.employeeId,
          )?.rate || 0,
      };
    });

    return commissionsMap;
  }, [employees, item]);

  const hasCommissions = Object.values(employeeCommissions).some(
    ({ rate }) => rate > 0,
  );

  const sections = useMemo(() => {
    if (!item) return [];

    const tempSections = [
      {
        name: 'Item Details',
        rows: [
          {
            label: 'Name',
            type: 'TEXT',
            value: [item.name || 'Unnamed'],
          },
          {
            label: 'Category',
            type: 'TEXT',
            value: [getTextForCategory(item.category)],
          },
          {
            label: 'Sales Tax',
            type: 'TEXT',
            value: [
              item.isTaxable ? `${productTaxRate * 100}%` : 'Non-taxable item',
            ],
          },
          {
            label: 'Default Price',
            type: 'TEXT',
            value: [formatDollarAmount(item.price)],
          },
        ] as SectionRow[],
      },
      {
        name: 'Financial Details',
        rows: [
          {
            label: 'Cost',
            type: 'TEXT',
            value: [item.cost ? formatDollarAmount(item.cost) : 'N/A'],
          },
          {
            label: 'Profit',
            type: 'TEXT',
            value: [
              item.cost ? formatDollarAmount(item.price - item.cost) : 'N/A',
            ],
          },
          hasCommissions && {
            label: 'Commission',
            type: 'TEXT',
            value: [
              item.commissionType
                ? getTextForCommissionType(item.commissionType)
                : 'N/A',
            ],
          },
        ].filter(Boolean) as SectionRow[],
      },
      hasCommissions && {
        name: 'Payouts',
        rows: Object.values(employeeCommissions ?? {}).map(
          ({ rate, name: employeeName }) => ({
            label: employeeName,
            type: 'TEXT',
            value: [`${rate * 100}%`],
          }),
        ) as SectionRow[],
      },
    ].filter(Boolean) as Section[];

    return tempSections;
  }, [item, productTaxRate, hasCommissions, employeeCommissions]);

  if (!item) return null;

  return (
    <SideBarModal
      modalOpen={!!isOpen}
      setModalOpen={onClose}
      name={name}
      sections={sections}
    />
  );
}

export default ItemSideBarModal;
